import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { firstValueFrom, switchMap, take } from 'rxjs';

import { ArmyBuilderConfig } from '../config';
import { HttpClientWithInFlightCache } from '../httpClient';
import { ToastService } from '../toast/toast';
import { DEVICE$ } from '../device';

/**
 * Registers this device for push notifications (native only — no web push, per the push
 * notifications spec), shows a toast for foreground pushes, and navigates to the route named
 * in a tapped notification's data payload.
 */
@Injectable({ providedIn: 'root' })
export class PushRegistrationService {
    private config = inject(ArmyBuilderConfig);
    private http = inject(HttpClientWithInFlightCache);
    private router = inject(Router);
    private toastService = inject(ToastService);

    private initialized = false;

    async init(): Promise<void> {
        if (this.initialized) return;
        this.initialized = true;

        if (!Capacitor.isNativePlatform()) {
            return;
        }

        try {
            await FirebaseMessaging.addListener('notificationReceived', (event) => {
                const notification = event.notification;
                this.toastService.showToast(notification.title || '', notification.body || '', {});
            });

            await FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
                const data = event.notification.data as Record<string, string> | undefined;
                if (!data?.route) {
                    return;
                }
                const route = data.route.replace(/:(\w+)/g, (placeholder, key) => data[key] ?? placeholder);
                this.router.navigateByUrl(route);
            });

            const permission = await FirebaseMessaging.requestPermissions();
            if (permission.receive !== 'granted') {
                return;
            }

            const device = await firstValueFrom(DEVICE$);
            const { token } = await FirebaseMessaging.getToken();
            const platform = Capacitor.getPlatform() as 'ios' | 'android';

            await firstValueFrom(
                this.http.put(
                    `${this.config.apiBaseUrl}/push/device-token`,
                    { deviceId: device.id, fcmToken: token, platform },
                    { requiresLogin: true }
                )
            );
        } catch (e) {
            this.initialized = false;
            throw e;
        }
    }

    async unregister(): Promise<void> {
        if (!Capacitor.isNativePlatform()) {
            return;
        }
        try {
            await firstValueFrom(
                DEVICE$.pipe(
                    take(1),
                    switchMap((device) =>
                        this.http.delete(`${this.config.apiBaseUrl}/push/device-token/${device.id}`, null, { requiresLogin: true })
                    )
                )
            );
        } finally {
            this.initialized = false;
        }
    }
}
